import React, { useState, useRef, useEffect } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import './SlideTab.scss';

interface NavbarButtonProps {
  btnName: string;
  btnClass: string;
  handleClickBtn: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export type TabItem = { name: string; value: any };

interface SlideTabProps {
  items: Array<TabItem>;
  defaultIndex?: number;
  style?: React.CSSProperties;
  className?: string;
  onSelect: (item: TabItem) => any;
}

/* eslint-disable react/prop-types */
const NavbarButton: React.FC<NavbarButtonProps> = ({
  btnName,
  btnClass,
  handleClickBtn,
}) => {
  return (
    <button
      type="button"
      className={btnClass}
      data-scroll-to={btnName}
      onClick={(e) => {
        if (!btnClass.includes('slideTab-link--active')) {
          handleClickBtn(e);
        }
      }}
    >
      {btnName}
    </button>
  );
};

export const SlideTab: React.FC<SlideTabProps> = ({
  items: tabItems = [],
  style,
  className = '',
  onSelect,
  defaultIndex,
}) => {
  const [offLeft, setOffLeft] = useState<number>(4);
  const [offWidth, setOffWidth] = useState<number>(0);
  const [activeIndex, setActiveIndex] = useState<number>(defaultIndex || 0);
  const slideTabRef = useRef<HTMLDivElement>(null);

  const handleSetOffsets = (btn: HTMLButtonElement) => {
    setOffLeft(btn.offsetLeft);
    setOffWidth(btn.offsetWidth);
  };

  const handleClickBtn = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number,
  ) => {
    const target = e.target as HTMLButtonElement;
    target.classList.add('slideTab-link--active');
    handleSetOffsets(target);
    setActiveIndex(index);
    handleAutoScroll(target);
    onSelect(tabItems[index]);
  };

  const handleAutoScroll = (target: HTMLButtonElement) => {
    const slideTab = slideTabRef.current?.querySelector('.slideTab-root') as
      | HTMLDivElement
      | undefined;
    if (!slideTab) {
      return;
    }
    // get current real offset
    const currentOffset: { left?: number; right?: number } = {};
    currentOffset.left = target.offsetLeft - slideTab.scrollLeft;
    currentOffset.right =
      slideTab.offsetWidth - (currentOffset.left + target.offsetWidth);
    // scroll at last item
    if (currentOffset.right < 30) {
      const nextItem = target.nextElementSibling as
        | HTMLButtonElement
        | undefined;
      const scrollSpace = nextItem
        ? nextItem.offsetWidth - currentOffset.right
        : 0;
      smoothScroll(slideTab, slideTab.scrollLeft + scrollSpace);
      return;
    }
    //scroll at first item
    if (currentOffset.left < 30) {
      const nextItem = target.previousElementSibling as
        | HTMLButtonElement
        | undefined;
      const scrollSpace = nextItem
        ? nextItem.offsetWidth - currentOffset.left
        : 0;
      smoothScroll(slideTab, slideTab.scrollLeft - scrollSpace);
    }
  };

  const smoothScroll = (element: HTMLElement, to: number, duration = 300) => {
    if (duration <= 0) return;
    const difference = to - element.scrollLeft;
    const perTick = (difference / duration) * 10;

    setTimeout(function () {
      element.scrollLeft = element.scrollLeft + perTick;
      if (element.scrollLeft === to) {
        return;
      }
      smoothScroll(element, to, duration - 10);
    }, 10);
  };

  useEffect(() => {
    const firstBtn = slideTabRef.current?.querySelector(
      `.slideTab-root > button:nth-of-type(${activeIndex + 1})`,
    ) as HTMLButtonElement | undefined;
    if (firstBtn) {
      handleSetOffsets(firstBtn);
    }
  }, []);

  return (
    <div
      className={'slide-tab-container ' + className}
      ref={slideTabRef}
      style={style}
    >
      <ScrollContainer className="slideTab-root">
        {tabItems.map((item, index) => (
          <NavbarButton
            key={index}
            btnName={item.name}
            btnClass={
              activeIndex === index
                ? 'slideTab-btn slideTab-btn--active'
                : 'slideTab-btn'
            }
            handleClickBtn={(e) => handleClickBtn(e, index)}
          />
        ))}
        <div
          className="slideTab-curr--pill"
          aria-hidden="true"
          style={{ left: `${offLeft - 3}px`, width: `${offWidth + 6}px` }}
        ></div>
      </ScrollContainer>
    </div>
  );
};
